import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { formatFullName } from 'shared/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { AccountActionPanel } from './account-action-panel';
import { AccountOrganizations } from './account-organizations';
import { ConflictAlert } from './conflict-alert';
import { formatDate } from './format-date';
import { EMAIL_SCHEMA } from './schemas';
import { useAccountEmailActions } from './account-email-actions.hooks';
import type { ManagedUser, Notice, Refresh } from './types';

interface AccountCardProps {
  user: ManagedUser;
  onChanged: (args: Refresh) => void;
  onNotice: (notice: Notice) => void;
}

const ADD_SCHEMA = z.object({ email: EMAIL_SCHEMA });

const holdsEmail = (user: ManagedUser, email: string) =>
  user.email === email || user.secondaryEmails.some(secondary => secondary.email === email);

/**
 * One account found by the email search: its emails with the actions support can take, and its
 * organizations. Deleted accounts can only release secondaries.
 *
 * @param props.onNotice - Called with a notice to show without searching again.
 * @param props.onChanged - Called after a successful write with the email to search again and the
 *   notice to show.
 */
export function AccountCard({ user, onChanged, onNotice }: AccountCardProps) {
  const addForm = useForm<z.infer<typeof ADD_SCHEMA>>({
    resolver: zodResolver(ADD_SCHEMA),
    defaultValues: { email: '' },
  });

  const {
    action,
    conflict,
    busy,
    targetError,
    open,
    cancel,
    previewFor,
    requestPreview,
    applyPrimaryChange,
    addSecondary,
    removeSecondary,
    releaseAndAdd,
  } = useAccountEmailActions({
    user,
    onNotice,
    onChanged: args => {
      addForm.reset();
      onChanged(args);
    },
  });

  return (
    <article
      className={`overflow-hidden rounded-lg border ${user.isDeleted ? 'border-dashed' : ''}`}
      data-testid={`account-${user.id}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 bg-muted px-4 py-3">
        <span className="font-semibold">{formatFullName(user)}</span>
        <Badge variant={user.isDeleted ? 'destructive' : 'secondary'}>
          {user.isDeleted ? 'Deleted' : 'Active'}
        </Badge>
        <span className="font-mono text-xs text-muted-foreground">{user.id}</span>
        <span className="ml-auto text-xs text-muted-foreground">
          Created {formatDate(user.createdAt)}
          {user.isDeleted && user.deletedAt && ` · Deleted ${formatDate(user.deletedAt)}`}
        </span>
      </div>

      <div className="space-y-2 px-4 py-3">
        <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Emails
        </div>
        <div className="divide-y">
          <div className="flex flex-wrap items-center gap-2 py-2">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
              <span className="font-medium [overflow-wrap:anywhere]">{user.email}</span>
              <Badge>Primary</Badge>
            </div>
            {!user.isDeleted && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => open({ type: 'change-target' })}>
                Change…
              </Button>
            )}
          </div>
          {user.secondaryEmails.map(secondary => (
            <div key={secondary.email} className="flex flex-wrap items-center gap-2 py-2">
              <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                <span className="font-medium [overflow-wrap:anywhere]">{secondary.email}</span>
                <Badge variant="outline">Secondary</Badge>
                <span className="text-xs text-muted-foreground">
                  added {formatDate(secondary.createdAt)}
                </span>
              </div>
              <div className="flex gap-2">
                {!user.isDeleted && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => previewFor(secondary.email)}>
                    Make primary
                  </Button>
                )}
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="text-destructive"
                  disabled={busy}
                  onClick={() => open({ type: 'remove', email: secondary.email })}>
                  {user.isDeleted ? 'Release' : 'Remove'}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {user.isDeleted ? (
          <p className="text-sm text-muted-foreground">
            This account is deleted, but its emails still match at sign-in and block other accounts
            from using them. Release an address to free it.
          </p>
        ) : (
          <Form {...addForm}>
            <form
              className="flex gap-2"
              onSubmit={addForm.handleSubmit(({ email }) => {
                if (holdsEmail(user, email)) {
                  addForm.setError('email', { message: 'This account already uses that address.' });
                  return;
                }
                open({ type: 'add', email });
              })}>
              <FormField
                control={addForm.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <Input
                        placeholder="Add a secondary email"
                        aria-label="Secondary email to add"
                        autoComplete="off"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button type="submit" variant="outline" disabled={busy}>
                Add
              </Button>
            </form>
          </Form>
        )}
        {conflict && (
          <ConflictAlert
            conflict={conflict}
            onRelease={({ email, holder }) => open({ type: 'release-add', email, holder })}
          />
        )}
      </div>

      {action && (
        <div className="border-t px-4 py-3">
          <AccountActionPanel
            action={action}
            user={user}
            busy={busy}
            targetError={targetError}
            onPreview={requestPreview}
            onApplyPrimary={applyPrimaryChange}
            onAdd={addSecondary}
            onRemove={removeSecondary}
            onReleaseAdd={releaseAndAdd}
            onCancel={cancel}
          />
        </div>
      )}

      <div className="border-t">
        <AccountOrganizations user={user} />
      </div>
    </article>
  );
}
